import React, { useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { ChevronDown, LogIn, LogOut, User2, AlertTriangle, ExternalLink, Menu, Sun, Moon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Disclaimer } from "@/components/Disclaimer";

const SECTIONS = [
  {
    key: "groepspringen",
    label: "Groepspringen",
    paths: [
      { to: "/groepspringen/waarden", label: "Waarden" },
      { to: "/groepspringen/checker", label: "Sprong Checker" },
      { to: "/groepspringen/afkortingen", label: "Afkortingen" },
    ],
  },
  {
    key: "micro_meso",
    label: "Micro / Meso",
    paths: [
      { to: "/micro-meso/waarden", label: "Waarden" },
      { to: "/micro-meso/checker", label: "Sprong Checker" },
      { to: "/micro-meso/afkortingen", label: "Afkortingen" },
    ],
  },
  {
    key: "individueel",
    label: "Individueel",
    paths: [
      { to: "/individueel/waarden", label: "Waarden" },
      { to: "/individueel/checker", label: "Sprong Checker" },
      { to: "/individueel/afkortingen", label: "Afkortingen" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const loc = useLocation();
  const [open, setOpen] = useState({ groepspringen: true, micro_meso: true, individueel: true });
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen gs-grain flex bg-background relative">
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-secondary text-white p-2 rounded-lg"
        data-testid="mobile-menu-toggle"
      >
        <Menu size={20} />
      </button>

      {/* Sidebar */}
      <aside
        className={`gs-sidebar fixed lg:static z-40 inset-y-0 left-0 w-72 transform transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 flex flex-col`}
        data-testid="sidebar"
      >
        <div className="px-6 py-7 border-b border-white/10">
          <Link to="/" className="block" data-testid="brand-link">
            <div className="text-xs uppercase tracking-[0.22em] text-orange-400 font-semibold">Turnsport</div>
            <h1 className="heading-display text-2xl text-white leading-tight mt-1">
              Groepspringen<br/>Sprongchecker
            </h1>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {SECTIONS.map((s) => (
            <div key={s.key} className="mb-2">
              <button
                onClick={() => setOpen({ ...open, [s.key]: !open[s.key] })}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-white/80 hover:text-white hover:bg-white/5 text-sm uppercase tracking-wider font-semibold"
                data-testid={`section-toggle-${s.key}`}
              >
                <span>{s.label}</span>
                <ChevronDown size={16} className={`transition-transform ${open[s.key] ? "rotate-180" : ""}`}/>
              </button>
              {open[s.key] && (
                <div className="mt-1 ml-2 space-y-0.5 border-l border-white/10 pl-3">
                  {s.paths.map((p) => (
                    <NavLink
                      key={p.to}
                      to={p.to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) => `block px-3 py-2 rounded-md text-sm ${isActive ? "active font-semibold" : "text-white/75 hover:bg-white/5 hover:text-white"}`}
                      data-testid={`nav-${p.to.replace(/\//g, "-")}`}
                    >
                      {p.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="mt-4 pt-4 border-t border-white/10 space-y-1">
            <NavLink to="/regelementen" onClick={() => setMobileOpen(false)} className={({ isActive }) => `block px-3 py-2 rounded-md text-sm ${isActive ? "active font-semibold" : "text-white/75 hover:bg-white/5 hover:text-white"}`} data-testid="nav-regelementen">
              Regelementen
            </NavLink>
            <NavLink to="/contact" onClick={() => setMobileOpen(false)} className={({ isActive }) => `block px-3 py-2 rounded-md text-sm ${isActive ? "active font-semibold" : "text-white/75 hover:bg-white/5 hover:text-white"}`} data-testid="nav-contact">
              Contact
            </NavLink>
            {user && (
              <NavLink to="/teams" onClick={() => setMobileOpen(false)} className={({ isActive }) => `block px-3 py-2 rounded-md text-sm ${isActive ? "active font-semibold" : "text-white/75 hover:bg-white/5 hover:text-white"}`} data-testid="nav-teams">
                Team beheer
              </NavLink>
            )}
            {user && (
              <NavLink to="/vereniging" onClick={() => setMobileOpen(false)} className={({ isActive }) => `block px-3 py-2 rounded-md text-sm ${isActive ? "active font-semibold" : "text-white/75 hover:bg-white/5 hover:text-white"}`} data-testid="nav-vereniging">
                Vereniging
              </NavLink>
            )}
            {user?.role === "admin" && (
              <NavLink to="/admin/gebruikers" onClick={() => setMobileOpen(false)} className={({ isActive }) => `block px-3 py-2 rounded-md text-sm ${isActive ? "active font-semibold" : "text-orange-300 hover:bg-white/5 hover:text-orange-200"}`} data-testid="nav-admin-users">
                Gebruikersbeheer
              </NavLink>
            )}
          </div>

          <div className="mt-6 px-3 text-xs text-white/40 space-y-1">
            <Link to="/privacy" className="block hover:text-white/70" data-testid="footer-privacy">Privacy</Link>
            <Link to="/voorwaarden" className="block hover:text-white/70" data-testid="footer-terms">Voorwaarden</Link>
          </div>
        </nav>

        <div className="border-t border-white/10 px-4 py-4 space-y-3">
          {/* Dark mode toggle */}
          <button
            onClick={toggleTheme}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-white/75 hover:text-white hover:bg-white/5 text-sm transition"
            data-testid="theme-toggle"
            aria-label={theme === "dark" ? "Schakel naar lichte modus" : "Schakel naar donkere modus"}
          >
            <span className="flex items-center gap-2">
              {theme === "dark" ? <Sun size={16}/> : <Moon size={16}/>}
              {theme === "dark" ? "Lichte modus" : "Donkere modus"}
            </span>
          </button>
          {user ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">
                  {user.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <div className="text-white font-medium">{user.name}</div>
                  <Link to="/mijn-sprongen" className="text-xs text-orange-300 hover:underline" data-testid="my-routines-link">Mijn sprongen</Link>
                </div>
              </div>
              <button onClick={logout} className="text-white/60 hover:text-white p-2 rounded" data-testid="logout-btn"><LogOut size={16}/></button>
            </div>
          ) : (
            <Link to="/login" className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-2.5 rounded-lg font-semibold transition" data-testid="sidebar-login-btn">
              <LogIn size={16}/> Inloggen
            </Link>
          )}
        </div>
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/30 z-30" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-0 min-h-screen relative z-10">
        <Disclaimer />
        <div className="px-6 lg:px-12 py-8 pt-16 lg:pt-8 max-w-6xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
